import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  useTheme, 
  Grid, 
  Card, 
  CardContent, 
  LinearProgress,
  Chip,
  alpha,
  CircularProgress,
} from "@mui/material";
import { Header } from "../../components/global/Header";
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import useSWR from 'swr';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { EmptyState } from '../../components/global';

const StatCard = ({ title, value, subtitle, icon, color, onClick }) => {
  const theme = useTheme();
  
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 3,
        boxShadow: theme.shadows[3],
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(color || theme.palette.primary.main, 0.05)} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6],
          borderColor: color || theme.palette.primary.main,
        } : {},
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: alpha(color || theme.palette.primary.main, 0.1),
              color: color || theme.palette.primary.main,
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            mb: subtitle ? 0.5 : 0,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data, error, isLoading } = useSWR('/dashboard/statistics', async () => {
    const response = await api.get('/dashboard/statistics');
    return response;
  });

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Dashboard" module="Main" subtitle="Σφάλμα φόρτωσης δεδομένων" />
        <EmptyState 
          title="Δεν ήταν δυνατή η φόρτωση των δεδομένων"
          description="Παρακαλώ δοκιμάστε ξανά αργότερα"
        />
      </Box>
    );
  }

  const stats = data?.statistics || {};
  const recentExpenses = data?.recentExpenses || [];
  const expensesTimeline = data?.expensesTimeline || [];
  const productionsSummary = data?.productionsSummary || [];

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format number
  const formatNumber = (num) => {
    return new Intl.NumberFormat('el-GR').format(num || 0);
  };

  // Prepare data for budget vs actual chart
  const budgetVsActualData = [
    {
      category: 'Προϋπολογισμός',
      amount: stats.totalBudget || 0,
    },
    {
      category: 'Πραγματικά',
      amount: stats.totalActualExpenses || 0,
    },
    {
      category: 'Προβλέψεις',
      amount: stats.totalPredictedExpenses || 0,
    },
  ];

  // Prepare data for expenses timeline
  const timelineData = [
    {
      id: 'Πραγματικά',
      data: expensesTimeline.map(item => ({
        x: item.month,
        y: item.actual,
      })),
    },
    {
      id: 'Προβλέψεις',
      data: expensesTimeline.map(item => ({
        x: item.month,
        y: item.predicted,
      })),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Dashboard" module="Main" subtitle="Επισκόπηση παραγωγών και εξόδων" />

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Συνολικές Παραγωγές"
            value={formatNumber(stats.totalProductions || 0)}
            icon={<TheatersOutlinedIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.primary.main}
            onClick={() => navigate('/productions/')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Συνολικά Έξοδα"
            value={formatCurrency(stats.totalExpenses || 0)}
            subtitle={`${formatCurrency(stats.totalActualExpenses || 0)} πραγματικά + ${formatCurrency(stats.totalPredictedExpenses || 0)} προβλέψεις`}
            icon={<MonetizationOnOutlinedIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.info.main}
            onClick={() => navigate('/expenses/')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Συνολικός Προϋπολογισμός"
            value={formatCurrency(stats.totalBudget || 0)}
            icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Χρήση Προϋπολογισμού"
            value={`${(stats.budgetUtilization || 0).toFixed(1)}%`}
            subtitle={`${formatCurrency(stats.totalActualExpenses || 0)} / ${formatCurrency(stats.totalBudget || 0)}`}
            icon={<TrendingUpOutlinedIcon sx={{ fontSize: 28 }} />}
            color={
              (stats.budgetUtilization || 0) > 100 
                ? theme.palette.error.main 
                : (stats.budgetUtilization || 0) > 80 
                  ? theme.palette.warning.main 
                  : theme.palette.success.main
            }
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Budget vs Actual Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              height: 400,
              boxShadow: theme.shadows[3],
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
              Προϋπολογισμός vs Πραγματικά Έξοδα
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveBar
                data={budgetVsActualData}
                keys={['amount']}
                indexBy="category"
                margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
                padding={0.3}
                valueFormat={formatCurrency}
                colors={[theme.palette.success.main, theme.palette.info.main, theme.palette.warning.main]}
                axisLeft={{
                  format: (value) => formatCurrency(value),
                }}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                }}
                theme={{
                  text: {
                    fill: theme.palette.text.primary,
                    fontSize: 12,
                  },
                  grid: {
                    line: {
                      stroke: theme.palette.divider,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Expenses Timeline Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              height: 400,
              boxShadow: theme.shadows[3],
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
              Έξοδα ανά Μήνα (Τελευταίοι 6 Μήνες)
            </Typography>
            <Box sx={{ height: 320 }}>
              {expensesTimeline.length > 0 ? (
                <ResponsiveLine
                  data={timelineData}
                  margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                  curve="monotoneX"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                  }}
                  axisLeft={{
                    format: (value) => formatCurrency(value),
                  }}
                  pointSize={8}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  colors={[theme.palette.info.main, theme.palette.warning.main]}
                  lineWidth={3}
                  theme={{
                    text: {
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    },
                    grid: {
                      line: {
                        stroke: theme.palette.divider,
                      },
                    },
                  }}
                  legends={[
                    {
                      anchor: 'top-right',
                      direction: 'column',
                      justify: false,
                      translateX: 0,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 80,
                      itemHeight: 20,
                      symbolSize: 12,
                      symbolShape: 'circle',
                    },
                  ]}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Δεν υπάρχουν δεδομένα για τα τελευταία 6 μήνες
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Expenses and Productions Summary */}
      <Grid container spacing={3}>
        {/* Recent Expenses */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              maxHeight: 500,
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
              Πρόσφατα Έξοδα
            </Typography>
            {recentExpenses.length > 0 ? (
              <Box>
                {recentExpenses.map((expense) => (
                  <Box
                    key={expense.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {expense.prod_descr?.p_descr || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {expense.pci_descr?.pci_descr || 'N/A'} • {expense.expense_beneficiaries_descr?.eb_company_name || 'N/A'}
                        </Typography>
                        {expense.exp_inv_no && (
                          <Typography variant="caption" color="text.secondary">
                            Τιμολόγιο: {expense.exp_inv_no}
                          </Typography>
                        )}
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                          {formatCurrency(expense.exp_amount || 0)}
                        </Typography>
                        {expense.exp_pred && (
                          <Chip
                            label="Πρόβλεψη"
                            size="small"
                            sx={{
                              mt: 0.5,
                              backgroundColor: alpha(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.main,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <EmptyState 
                title="Δεν υπάρχουν πρόσφατα έξοδα"
                description="Όταν προστεθούν νέα έξοδα, θα εμφανίζονται εδώ"
              />
            )}
          </Paper>
        </Grid>

        {/* Productions Summary */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: theme.shadows[3],
              maxHeight: 500,
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
              Κατάσταση Παραγωγών
            </Typography>
            {productionsSummary.length > 0 ? (
              <Box>
                {productionsSummary.map((production) => (
                  <Box
                    key={production.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => navigate(`/productions/update/${production.id}`)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {production.name}
                      </Typography>
                      {production.status === 'over' && (
                        <ErrorOutlineIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                      )}
                      {production.status === 'warning' && (
                        <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      )}
                      {production.status === 'ok' && (
                        <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                      )}
                    </Box>
                    <Box mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        {production.totalEpisodes} Επεισόδια
                      </Typography>
                    </Box>
                    <Box mb={1}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Χρήση Προϋπολογισμού
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {production.utilization.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(production.utilization, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          backgroundColor: theme.palette.action.hover,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor:
                              production.status === 'over'
                                ? theme.palette.error.main
                                : production.status === 'warning'
                                  ? theme.palette.warning.main
                                  : theme.palette.success.main,
                          },
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Προϋπολογισμός: {formatCurrency(production.budget)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Έξοδα: {formatCurrency(production.actualExpenses)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <EmptyState 
                title="Δεν υπάρχουν παραγωγές"
                description="Προσθέστε παραγωγές για να εμφανίζονται εδώ"
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
